/**
 * effortWords — turn a heart-rate ratio into a FEELING word. The HR screens
 * never print a BPM number; a 3-level word (Easy / Working / Hard) tolerates the
 * one-length HR lag and reads under a wet thumb at arm's length.
 *
 * Buckets ride the existing hrZoneColor() thresholds (ratio of swim peak):
 *   ratio < 0.65         → Easy
 *   0.65 <= ratio < 0.85 → Working  (per-lap)  /  Moderate (set badge)
 *   ratio >= 0.85        → Hard
 *
 * No HR data (companion-only future case, missing samples) → null. Omit the
 * word; never guess. The module is agnostic to where the numbers come from —
 * callers pass lap.avgHr + dw.maxHr (peak) per lap, and a set-average HR vs
 * dw.maxHr for the badge.
 *
 * Pure module. No BPM string ever leaves this file.
 */
import { COLORS } from "./designTokens";

const EASY_MAX = 0.65; // ratio < this → Easy
const HARD_MIN = 0.85; // ratio >= this → Hard

/** ratio = avgHr / peakHr, or null when HR is unavailable. */
function effortRatio(avgHr: number | null, peakHr: number | null): number | null {
  if (avgHr === null || peakHr === null || peakHr === 0) return null;
  return avgHr / peakHr;
}

export function lapEffortWord(
  avgHr: number | null,
  peakHr: number | null,
): "Easy" | "Working" | "Hard" | null {
  const ratio = effortRatio(avgHr, peakHr);
  if (ratio === null) return null;
  if (ratio < EASY_MAX) return "Easy";
  if (ratio < HARD_MIN) return "Working";
  return "Hard";
}

export function setEffortBadge(
  avgHr: number | null,
  peakHr: number | null,
): "Easy" | "Moderate" | "Hard" | null {
  const ratio = effortRatio(avgHr, peakHr);
  if (ratio === null) return null;
  if (ratio < EASY_MAX) return "Easy";
  if (ratio < HARD_MIN) return "Moderate";
  return "Hard";
}

/**
 * Map a feeling word to its ramp color. The lap word and the set badge share
 * the middle color (Working === Moderate === yellow).
 *
 * Easy `#4A6A8A` matches hrZoneColor()'s easy rung; it has no named COLORS key,
 * so it is a local const. The other two come from designTokens.
 */
const EASY_COLOR = "#4A6A8A"; // matches hrZoneColor() easy rung

export function effortColor(word: string): string {
  switch (word) {
    case "Easy":
      return EASY_COLOR;
    case "Working":
    case "Moderate":
      return COLORS.yellow; // #FBC02D
    case "Hard":
      return COLORS.coralRed; // #FF5722
    default:
      return COLORS.textMuted;
  }
}
